import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import {
  ALL_FACILITIES,
  selectedFacilityIdAtom
} from '@/hooks/useFacilitiesQuery';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { useSnackbar } from '@/hooks/useSnackbar';
import useIsScreenerAllFacilitiesSelected from '@/pages/CRM/Screener/Leads/AllLeads/hooks/useIsScreenerAllFacilitiesSelected';

const getReplacementFacilityId = (
  currentUser: ReturnType<typeof useCurrentUser>['data']
) => {
  if (!currentUser?.user_facility_accesses?.length) {
    return null;
  }

  const homeFacilityAccess = currentUser.user_facility_accesses.find(
    (access) => access.facility_id === currentUser.facility_id
  );

  return homeFacilityAccess?.facility_id
    ? homeFacilityAccess.facility_id
    : (currentUser.user_facility_accesses[0]?.facility_id ?? null);
};

export function useMobileViewportFacilitySelection() {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobileViewport = useIsMobileViewport();
  const currentUser = useCurrentUser().data;
  const { showSnackbar } = useSnackbar();
  const [selectedFacilityId, setSelectedFacilityId] = useAtom(
    selectedFacilityIdAtom
  );
  const isAllFacilitiesSelected = useIsScreenerAllFacilitiesSelected();

  useEffect(() => {
    const hasFacilityAccess =
      !!selectedFacilityId &&
      currentUser?.user_facility_accesses?.some(
        (access) => access.facility_id === selectedFacilityId
      );
    const shouldRepairSelection =
      selectedFacilityId == null ||
      selectedFacilityId === ALL_FACILITIES ||
      isAllFacilitiesSelected ||
      !hasFacilityAccess;

    if (!currentUser || !isMobileViewport || !shouldRepairSelection) {
      return;
    }

    const replacementFacilityId = getReplacementFacilityId(currentUser);
    if (replacementFacilityId) {
      const replacementFacility = currentUser.user_facility_accesses?.find(
        (access) => access.facility_id === replacementFacilityId
      );

      navigate(location.pathname, { replace: true });
      setSelectedFacilityId(replacementFacilityId);

      if (selectedFacilityId === ALL_FACILITIES || isAllFacilitiesSelected) {
        showSnackbar({
          message: `All Facilities unavailable on mobile. ${replacementFacility?.facility?.name || 'Your facility'} is now selected.`,
          severity: 'info'
        });
      }
    }
  }, [
    isMobileViewport,
    selectedFacilityId,
    isAllFacilitiesSelected,
    navigate,
    location.pathname,
    setSelectedFacilityId,
    currentUser,
    showSnackbar
  ]);
}
